import { useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { listBuiltinConnectorTypes } from '@brunel/shared';
import type { Guard } from '../App';
import type {
  AiConnectionStatusRow,
  AiLocalRuntimeStatusRow,
  CodexAutonomyStatusRow,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  ProviderUsageLatest,
} from '../lib/data';
import { buildConnectorCenterGroups } from '../lib/connector-center-view';
import { errorMessage } from '../lib/mutations';
import type { ConnectorRegistrySnapshot } from '../lib/connector-registry';
import type { SystemConnectorType, SystemMonitorCard } from '../lib/systems-monitor';
import { AiConnectionsSettings } from './AiConnectionsSettings';
import { SupabaseCliCard } from './SupabaseCliCard';
import '../connector-center.css';

/** A Supabase or other service host must not be written into subscriber-visible settings text. */
function subscriberVisibleFactLink(link: string | null): string | null {
  if (link === null || link.trim() === '') return null;
  try {
    const host = new URL(link).hostname.toLowerCase();
    if (host === 'supabase.com' || host.endsWith('.supabase.com') || host === 'supabase.co' || host.endsWith('.supabase.co')) {
      return null;
    }
  } catch {
    return null;
  }
  return link;
}

function connectionModeLabel(value: string | null): string {
  if (value === null) return 'Not synchronized';
  if (value === 'node_bound') return 'Node-bound';
  if (value === 'external_mcp') return 'External MCP';
  if (value === 'local_no_auth') return 'Local · no auth';
  if (value === 'commandos_vault') return 'CommandOS vault';
  if (value === 'external_subscription') return 'External subscription';
  if (value === 'unavailable') return 'Unavailable';
  return value.replaceAll('_', ' ');
}

function whenLabel(value: string | null): string {
  if (!value) return 'Unknown';
  const time = Date.parse(value);
  if (Number.isNaN(time)) return value;
  return new Date(time).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface ConnectorCenterProps {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly nodes: readonly NodePresence[];
  readonly providerUsage: readonly ProviderUsageLatest[];
  readonly connectionStatuses: readonly AiConnectionStatusRow[];
  readonly localRuntimeStatuses: readonly AiLocalRuntimeStatusRow[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly codexAutonomyStatuses: readonly CodexAutonomyStatusRow[];
  readonly registry?: ConnectorRegistrySnapshot;
  readonly systems?: readonly SystemMonitorCard[];
  readonly focusedSystemType?: SystemConnectorType | null;
  readonly onRefreshRegistry?: () => Promise<void> | void;
  readonly onChanged: () => void;
  readonly onOpenLocalDetail: (nodeId: string) => void;
}

export function ConnectorCenter({
  client,
  guard,
  orgId,
  nodes,
  providerUsage,
  connectionStatuses,
  localRuntimeStatuses,
  nodeOfferings,
  modelOfferings,
  codexAutonomyStatuses,
  registry = {
    types: listBuiltinConnectorTypes(), connectors: [], vaultAvailable: false,
    loaded: false, error: null,
  },
  systems = [],
  focusedSystemType = null,
  onRefreshRegistry,
  onChanged,
  onOpenLocalDetail,
}: ConnectorCenterProps): ReactElement {
  const [busyConnector, setBusyConnector] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const groups = useMemo(() => buildConnectorCenterGroups({
    registryTypes: registry.types,
    connectors: registry.connectors,
    vaultAvailable: registry.vaultAvailable,
    registryLoaded: registry.loaded && registry.error === null,
    nodes,
    connectionStatuses,
    localRuntimeStatuses,
    providerUsage,
    nodeOfferings,
    modelOfferings,
  }), [
    registry, nodes, connectionStatuses, localRuntimeStatuses,
    providerUsage, nodeOfferings, modelOfferings,
  ]);

  // Owner-verified projection of existing tenant evidence into canonical connector rows (#557).
  function synchronizeInventory(): void {
    if (syncing) return;
    setError(null);
    setOk(null);
    void guard('post_owner_command', async () => {
      setSyncing(true);
      try {
        const { data, error: transportError } = await client.rpc('sync_connector_inventory', {
          request: { orgId },
        });
        const result = data as { ok?: boolean; error?: { code?: string } } | null;
        if (transportError || !result?.ok) {
          setError(errorMessage(result?.error?.code ?? 'internal'));
          return;
        }
        setOk('Connector inventory synchronized from current evidence. Missing evidence stays Unknown.');
        await Promise.resolve(onRefreshRegistry?.());
      } finally {
        setSyncing(false);
      }
    });
  }

  async function refreshConnector(connectorId: string): Promise<void> {
    setBusyConnector(connectorId);
    setError(null);
    setOk(null);
    try {
      const { data, error: transportError } = await client.rpc('refresh_connector_metadata', {
        request: { orgId, connectorId },
      });
      const result = data as { ok?: boolean; error?: { code?: string } } | null;
      if (transportError || !result?.ok) {
        setError(errorMessage(result?.error?.code ?? 'internal'));
        return;
      }
      setOk('Connector refresh requested. Unknown values stay Unknown until a supported source reports them.');
      onChanged();
    } finally {
      setBusyConnector(null);
    }
  }

  return (
    <div className="connector-center stack" aria-label="Connector Center">
      <header className="connector-center-intro">
        <div>
          <h2>Connector Center</h2>
          <p className="muted">
            One view of Brunel connections, executable runtimes, external subscription AI, and registered services.
            Credentials remain in their supported boundary; this page never reads browser cookies or secret plaintext.
          </p>
        </div>
        <div className="row wrap connector-center-actions">
          <button
            type="button"
            onClick={() => {
              void Promise.resolve(onRefreshRegistry?.());
            }}
          >
            {registry.loaded ? 'Refresh center' : 'Try connector read'}
          </button>
          <button type="button" disabled={syncing} onClick={synchronizeInventory}>
            {syncing ? 'Synchronizing…' : 'Synchronize inventory'}
          </button>
        </div>
      </header>

      {!registry.vaultAvailable && (
        <p className="connector-center-notice">
          Secure CommandOS vault credentials are not available. Secret-backed services such as Supabase remain metadata-only / unavailable, and health stays Unknown.
        </p>
      )}

      {(error ?? registry.error) && <p role="alert">{error ?? registry.error}</p>}
      {ok && <p role="status" className="muted">{ok}</p>}

      {focusedSystemType && (
        <SystemDetail system={systems.find((row) => row.typeKey === focusedSystemType) ?? null} />
      )}

      {registry.loaded && registry.connectors.length === 0 && (
        <p className="connector-center-notice" role="status">
          Connector inventory not synchronized. Every surface below stays Unknown until canonical connector rows exist;
          Brunel does not rebuild them from local catalogs or node probes.
        </p>
      )}

      <div className="connector-center-groups">
        {groups.map((group) => (
          <section key={group.key} className="connector-center-group" aria-label={group.title}>
            <header>
              <h3>{group.title}</h3>
              <small className="muted">{group.description}</small>
            </header>
            <div className="connector-center-grid">
              {group.rows.map((row) => (
                <article
                  key={row.id}
                  className="connector-center-card"
                  data-health={row.health.toLowerCase()}
                  aria-label={row.nodeName ? `${row.label} on ${row.nodeName}` : row.label}
                >
                  <div className="row spread connector-center-card-heading">
                    <div>
                      <strong>{row.label}</strong>
                      {row.nodeName && <small>{row.nodeName}</small>}
                    </div>
                    <span className="tag">{connectionModeLabel(row.connectionMode)}</span>
                  </div>
                  <p className="connector-center-subtitle">{row.subtitle}</p>
                  <dl className="connector-center-facts">
                    <div><dt>Kind</dt><dd>{row.kindLabel}</dd></div>
                    <div><dt>Provider</dt><dd>{row.provider}</dd></div>
                    <div><dt>State</dt><dd>{row.statusLabel}</dd></div>
                    <div><dt>Health</dt><dd>{row.healthLabel}</dd></div>
                    <div><dt>Runtime</dt><dd>{row.runtimeLabel}</dd></div>
                    <div><dt>Models</dt><dd>{row.models.length > 0 ? row.models.join(' · ') : 'Unknown / not applicable'}</dd></div>
                    <div><dt>Allowance</dt><dd>{row.allowanceLabel}</dd></div>
                    <div><dt>Last evidence</dt><dd>{whenLabel(row.lastEvidenceAt)}</dd></div>
                  </dl>
                  {row.note && <small>{row.note}</small>}
                  <div className="row wrap connector-center-actions">
                    {row.canOpenLocalDetail && row.nodeId && (
                      <button type="button" onClick={() => onOpenLocalDetail(row.nodeId!)}>
                        Local details
                      </button>
                    )}
                    {row.canRefreshConnector && row.connectorId && (
                      <button
                        type="button"
                        disabled={busyConnector !== null}
                        onClick={() => { void refreshConnector(row.connectorId!); }}
                      >
                        {busyConnector === row.connectorId ? 'Refreshing…' : 'Refresh connector'}
                      </button>
                    )}
                    <details>
                      <summary>Details / source</summary>
                      <div className="connector-center-detail stack tight">
                        <span>Type: {row.typeKey}</span>
                        <span>Source: {row.sourceLabel}</span>
                        <span>Connector ID: {row.connectorId ?? 'Not registered'}</span>
                        <span>Connection key: {row.connectionKey ?? 'Not applicable'}</span>
                        <span>Source key: {row.sourceKey ?? 'Not synchronized'}</span>
                      </div>
                    </details>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

      <SupabaseCliCard client={client} guard={guard} orgId={orgId} nodes={nodes} />

      <details className="connector-center-runtime-controls">
        <summary>Runtime connection & Codex permission controls</summary>
        <p className="muted">
          These are the existing node-scoped controls. Connect/sign-in and usage checks are shown only for allowlisted supported runtimes.
          There is no generic revoke button because Brunel does not pretend registry revocation signs out a provider session.
        </p>
        <AiConnectionsSettings
          client={client}
          guard={guard}
          orgId={orgId}
          nodes={nodes}
          providerUsage={providerUsage}
          connectionStatuses={connectionStatuses}
          codexAutonomyStatuses={codexAutonomyStatuses}
          onChanged={() => {
            onChanged();
          }}
        />
      </details>
    </div>
  );
}

function SystemDetail({ system }: { readonly system: SystemMonitorCard | null }): ReactElement {
  if (!system) {
    return <section className="connector-center-system-detail"><p className="muted">System evidence is Unknown.</p></section>;
  }
  return (
    <section className="connector-center-system-detail" aria-label={`${system.label} system detail`}>
      <header className="row spread wrap">
        <div>
          <h3>{system.label}</h3>
          <p>{system.summary}</p>
        </div>
        <span className="system-status" data-status={system.status.toLowerCase()}>{system.status}</span>
      </header>
      <dl className="connector-center-facts">
        {system.facts.map((fact) => {
          const link = subscriberVisibleFactLink(fact.link);
          return (
            <div key={fact.id}>
              <dt>{fact.label}</dt>
              <dd>
                {link
                  ? <a href={link} target="_blank" rel="noreferrer noopener">{fact.value}</a>
                  : fact.value}
                {fact.detail ? <small className="muted">{fact.detail}</small> : null}
              </dd>
            </div>
          );
        })}
        <div><dt>Evidence observed</dt><dd>{whenLabel(system.observedAt)}</dd></div>
        <div><dt>Last successful check</dt><dd>{whenLabel(system.lastSuccessfulAt)}</dd></div>
        <div><dt>Source</dt><dd>{system.source}</dd></div>
      </dl>
    </section>
  );
}
